import { useAppSelector } from "hooks/hook";
import { Check, ChevronDown } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getAccessibleRestaurants } from "resbutler-utils/utils/restaurantUtils";
import { catchExceptionCallback } from "resbutler-ui/error";
import { setUser } from "store/reducers/rootSlice";
import useUrlParams from "../../hooks/useUrlParams";
import { readClaims, setActiveRestaurant } from "core/auth";

const initialsFromName = (name: string): string => {
  const tokens = name.split(/\s+/).filter(Boolean);
  const first = tokens[0]?.[0] ?? "";
  const second = tokens[1]?.[0] ?? "";
  return (first + second).toUpperCase() || name.slice(0, 2).toUpperCase() || "??";
};

const RestaurantSwitcher = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [restaurantId, date] = useUrlParams();

  const { restaurants, user } = useAppSelector((state: any) => ({
    restaurants: getAccessibleRestaurants(state.root.restaurants, state.root.user),
    user: state.root.user,
  }));

  const restaurantIds = useMemo(() => (restaurants ? Object.keys(restaurants) : []), [restaurants]);
  const current = restaurants?.[restaurantId];
  const multi = restaurantIds.length > 1;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const selectRestaurant = async (id: string) => {
    setOpen(false);
    if (id === restaurantId || switching) return;
    try {
      setSwitching(true);
      await setActiveRestaurant(id); // POST /staff/active-restaurant + getIdToken(true)
      const next = await readClaims();
      if (next && user) dispatch(setUser({ ...user, customClaims: next })); // re-mint active restaurant's grants
      navigate(`${window.location.pathname}#${date}/${id}`);
    } catch (e) {
      catchExceptionCallback(e);
      toast.error("Could not switch restaurant");
    } finally {
      setSwitching(false);
    }
  };

  if (!current) return null;
  const currentInitials = initialsFromName(current.name);

  if (!multi) {
    return (
      <div className="ru-venue-switch single" aria-label={`Restaurant: ${current.name}`}>
        <span className="ru-venue-flag">{currentInitials}</span>
        <div className="ru-venue-meta">
          <span className="ru-venue-name">{current.name}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="ru-venue-switch" ref={wrapRef}>
      <button
        type="button"
        className={`ru-venue-trigger${switching ? " is-switching" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-busy={switching}
        disabled={switching}
        aria-label="Switch restaurant"
      >
        <span className="ru-venue-flag">{switching ? <span className="ru-venue-spinner" aria-hidden /> : currentInitials}</span>
        <div className="ru-venue-meta">
          <span className="ru-venue-name">{current.name}</span>
          <span className="ru-venue-sub">{switching ? "switching…" : `${restaurantIds.length} restaurants · switch`}</span>
        </div>
        <ChevronDown size={12} aria-hidden />
      </button>

      {open && (
        <div className="ru-venue-menu" role="listbox" aria-label="Available restaurants">
          {restaurantIds.map((id) => {
            const r = restaurants[id];
            const active = id === restaurantId;
            return (
              <button key={id} type="button" role="option" aria-selected={active} className={`ru-venue-row${active ? " active" : ""}`} disabled={switching} onClick={() => selectRestaurant(id)}>
                <span className="ru-venue-flag">{initialsFromName(r.name)}</span>
                <span className="ru-venue-row-name">{r.name}</span>
                {active && <Check size={14} aria-hidden className="ru-venue-row-check" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RestaurantSwitcher;
